/*
 * Koco Loco phone hero: the portrait pixel-art room, very gently alive (same life as the phone room, koco-hero-room.js).
 *
 * The artwork is never redrawn or changed. Every frame starts from the original picture and only moves or lights
 * pixels that already exist in it, always by whole "art pixels" (the picture's own pixel grid is about 5 pixels in the 941 x 1672 picture),
 * so edges stay hard: no smoothing, no blur, no morphing, no new colours (a few effects only make existing colours
 * a touch lighter or darker). The loop is 8 seconds and every motion repeats a whole number of times in it, so the
 * last frame leads straight back into the first.
 *
 *   Koco         his chest rises and falls by two art pixels; the tip of his tail sways by one
 *   cable        the loop of the earphone cable sways a little
 *   lamp         the warm lamp pulses a few per cent
 *   TV           a slow scan line and a faint flicker on the screen
 *   city         a few lit windows and lanterns dim and come back
 *   plants       the three hanging plants sway, more at the tips than where they hang
 *   sky          the clouds and stars drift one art pixel (the far layer)
 *   dust         a few specks float up through the warm light (the near layer)
 *
 * Only runs on phones (< 750px), only while the hero is on screen, and not at all for visitors who ask for reduced motion
 * (they keep the still picture).
 */
(() => {
  const hero = document.querySelector('.hero__container');
  if (!hero || hero.dataset.kocoRoomM) return;
  hero.dataset.kocoRoomM = 'true';

  const phone = window.matchMedia('(max-width: 749px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const sourceUrl = hero.dataset.roomSrcM;
  if (!sourceUrl || !phone.matches || reducedMotion.matches) return;

  const W = 941;
  const H = 1672;
  const ART = 5; // picture pixels per art pixel in the 941 x 1672 picture
  const LOOP = 8; // seconds
  const FPS = 12;
  const FRAMES = LOOP * FPS;
  const TAU = Math.PI * 2;

  const q = (value) => Math.round(value / ART) * ART; // whole art pixels
  const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
  const smooth = (v) => {
    const t = clamp(v, 0, 1);
    return t * t * (3 - 2 * t);
  };

  // A small seeded generator, so every visitor sees the same room
  const seeded = (seed) => {
    let a = seed >>> 0;
    return () => {
      a = (a + 0x6d2b79f5) >>> 0;
      let t = a;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  };

  const image = new Image();
  image.decoding = 'async';
  image.crossOrigin = 'anonymous'; // the picture comes from Shopify's CDN: its pixels may only be read with this
  image.onload = start;
  image.onerror = () => {}; // without it the still picture simply stays
  image.src = sourceUrl;

  function start() {
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    canvas.className = 'koco-room-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    const ctx = canvas.getContext('2d', { alpha: false, willReadFrequently: false });
    if (!ctx) return;
    ctx.imageSmoothingEnabled = false;

    // the original picture, as numbers
    const scratch = document.createElement('canvas');
    scratch.width = W;
    scratch.height = H;
    const sctx = scratch.getContext('2d', { willReadFrequently: true });
    sctx.drawImage(image, 0, 0, W, H);
    let pixels;
    try {
      pixels = sctx.getImageData(0, 0, W, H);
    } catch (error) {
      return; // the picture cannot be read: keep the still picture
    }
    const src = pixels.data; // never modified
    const imageData = ctx.createImageData(W, H);
    const out = imageData.data;
    const idx = (x, y) => (y * W + x) * 4;

    /* ------------------------------------------------------------------------------------------------------------
     * Layers of pixels that move on their own over the picture (plants, the cable, the tail). Their pixels are found
     * by colour inside a box, grown by a few pixels to take their dark outline along, and the places they leave are
     * filled from the nearest pixel beside them, so nothing is left behind when they shift.
     * ---------------------------------------------------------------------------------------------------------- */
    const base = new Uint8ClampedArray(src); // the picture with the moving pixels lifted out and their places filled

    function makeLayer(box, isLayerColour, grow, weightAt) {
      const [x0, y0, x1, y1] = box;
      const w = x1 - x0 + 1;
      const h = y1 - y0 + 1;
      const mask = new Uint8Array(w * h);
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const i = idx(x0 + x, y0 + y);
          if (isLayerColour(src[i], src[i + 1], src[i + 2])) mask[y * w + x] = 1;
        }
      }
      if (grow > 0) {
        const grown = new Uint8Array(mask);
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            if (!mask[y * w + x]) continue;
            for (let dy = -grow; dy <= grow; dy++) {
              for (let dx = -grow; dx <= grow; dx++) {
                const nx = x + dx;
                const ny = y + dy;
                if (nx >= 0 && ny >= 0 && nx < w && ny < h) grown[ny * w + nx] = 1;
              }
            }
          }
        }
        mask.set(grown);
      }
      const pixels = [];
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          if (!mask[y * w + x]) continue;
          const px = x0 + x;
          const py = y0 + y;
          pixels.push({ x: px, y: py, weight: weightAt(px, py), color: [src[idx(px, py)], src[idx(px, py) + 1], src[idx(px, py) + 2]] });
          // fill the place from the nearest free pixel on the same row
          let fill = null;
          for (let d = 1; d <= 16 && !fill; d++) {
            const lx = x - d;
            const rx = x + d;
            if (lx >= 0 && !mask[y * w + lx]) fill = idx(x0 + lx, py);
            else if (rx < w && !mask[y * w + rx]) fill = idx(x0 + rx, py);
          }
          if (fill !== null) {
            const i = idx(px, py);
            base[i] = src[fill];
            base[i + 1] = src[fill + 1];
            base[i + 2] = src[fill + 2];
          }
        }
      }
      return pixels;
    }

    const isGreenLeaf = (r, g, b) => (g > r + 6 && g > b + 14) || (g >= r - 12 && g > b + 34 && g > 90);
    const isCable = (r, g, b) => r > 170 && g > 125 && b < 150 && r - b > 45 && g > b + 25;
    const isTail = (r, g, b) => (g > b + 40 && g > 90 && r > 70 && r < 215 && g >= r - 12) || (r > 150 && g > 105 && b < 110 && g > b + 45);

    // hanging plants: anchored where they hang, free at the tips (weight grows with the distance below the top)
    const vineBoxes = [
      { box: [190, 20, 350, 530], top: 20, span: 500, phase: 0 },
      { box: [350, 125, 465, 530], top: 125, span: 400, phase: 1.7 },
      { box: [792, 0, 941, 392], top: 0, span: 390, phase: 3.1 },
    ];
    const vines = vineBoxes.map((v) =>
      makeLayer(v.box, isGreenLeaf, 3, (x, y) => smooth(((y - v.top) / v.span) * 1.15)) .map((p) => ({ ...p, phase: v.phase }))
    );

    // the earphone cable: the loop below his chest, against the dark sofa (kept still where it crosses the pillow)
    const cable = makeLayer([190, 1182, 266, 1236], isCable, 2, (x, y) => smooth((y - 1184) / 22) * smooth((266 - x) / 26));

    // the tail: still where it joins his trousers, free at the curl
    const tail = makeLayer([756, 1090, 882, 1232], isTail, 5, (x, y) => {
      const d = Math.hypot(x - 768, y - 1192);
      return smooth((d - 14) / 70);
    });

    /* ------------------------------------------------------------------------------------------------------------
     * Boxes whose pixels are re-sampled a few pixels away (the chest, the sky).
     * ---------------------------------------------------------------------------------------------------------- */
    const edgeWeight = (x, y, box, feather) => {
      const [x0, y0, x1, y1] = box;
      return smooth(Math.min(x - x0, x1 - x, y - y0, y1 - y) / feather);
    };

    const chest = [204, 1040, 392, 1150];
    const sky = [[400, 466, 748, 568]];

    /* ------------------------------------------------------------------------------------------------------------
     * Lights: lit windows and lanterns of the city (found by colour), the lamp, the TV screen.
     * ---------------------------------------------------------------------------------------------------------- */
    const lights = [];
    {
      const [x0, y0, x1, y1] = [398, 650, 782, 815];
      const w = x1 - x0 + 1;
      const h = y1 - y0 + 1;
      const lit = new Uint8Array(w * h);
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const i = idx(x0 + x, y0 + y);
          const r = src[i];
          const g = src[i + 1];
          const b = src[i + 2];
          if (r > 170 && r > b + 60 && g > 40) lit[y * w + x] = 1;
        }
      }
      // join the pixels of one window (neighbours within 4 px) and keep the smaller groups: windows and lanterns
      const label = new Int32Array(w * h);
      let n = 0;
      for (let s = 0; s < w * h; s++) {
        if (!lit[s] || label[s]) continue;
        n += 1;
        const stack = [s];
        label[s] = n;
        const group = [];
        while (stack.length) {
          const p = stack.pop();
          group.push(p);
          const px = p % w;
          const py = (p - px) / w;
          for (let dy = -4; dy <= 4; dy++) {
            for (let dx = -4; dx <= 4; dx++) {
              const nx = px + dx;
              const ny = py + dy;
              if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
              const m = ny * w + nx;
              if (lit[m] && !label[m]) {
                label[m] = n;
                stack.push(m);
              }
            }
          }
        }
        if (group.length >= 8 && group.length <= 900) {
          lights.push(group.map((p) => idx(x0 + (p % w), y0 + Math.floor(p / w))));
        }
      }
    }
    const rand = seeded(20261008);
    const flicker = lights.map(() => ({
      on: rand() < 0.7, // about half of them take part
      start: rand() * LOOP,
      length: 0.5 + rand() * 1.1,
      depth: 0.34 + rand() * 0.22, // how dim they get (share of the original light that remains)
    }));

    // specks of dust in the warm light of the lamp
    const dust = Array.from({ length: 16 }, () => ({
      x: 50 + rand() * 200,
      y: 560 + rand() * 300,
      rise: 18 + rand() * 30, // pixels climbed per loop
      drift: (rand() - 0.5) * 24,
      phase: rand(),
      size: rand() < 0.3 ? 10 : 5,
    }));

    /* ------------------------------------------------------------------------------------------------------------
     * One frame.
     * ---------------------------------------------------------------------------------------------------------- */
    const lamp = [66, 650, 170, 840];
    const tv = [822, 805, 941, 1062];

    function render(frame) {
      const t = frame / FPS; // seconds into the loop
      const phase = t / LOOP; // 0 to 1
      out.set(base);

      // Koco breathes: the chest rises by up to two art pixels, two breaths per loop
      {
        const lift = 9.5 * (0.5 - 0.5 * Math.cos(TAU * 2 * phase));
        const [x0, y0, x1, y1] = chest;
        for (let y = y0; y <= y1; y++) {
          for (let x = x0; x <= x1; x++) {
            const shift = q(edgeWeight(x, y, chest, 34) * lift);
            if (!shift) continue;
            const from = idx(x, y + shift);
            const to = idx(x, y);
            out[to] = src[from];
            out[to + 1] = src[from + 1];
            out[to + 2] = src[from + 2];
          }
        }
      }

      // the far layer: clouds and stars drift one art pixel
      {
        const drift = 8.5 * Math.sin(TAU * phase);
        for (const box of sky) {
          const [x0, y0, x1, y1] = box;
          for (let y = y0; y <= y1; y++) {
            for (let x = x0; x <= x1; x++) {
              const shift = q(edgeWeight(x, y, box, 34) * drift);
              if (!shift) continue;
              const from = idx(clamp(x - shift, x0, x1), y);
              const to = idx(x, y);
              out[to] = src[from];
              out[to + 1] = src[from + 1];
              out[to + 2] = src[from + 2];
            }
          }
        }
      }

      // the layers of pixels that sway
      const place = (list, move, vertical) => {
        for (const p of list) {
          const shift = q(p.weight * move(p));
          const x = vertical ? p.x : p.x + shift;
          const y = vertical ? p.y + shift : p.y;
          if (x < 0 || y < 0 || x >= W || y >= H) continue;
          const i = idx(x, y);
          out[i] = p.color[0];
          out[i + 1] = p.color[1];
          out[i + 2] = p.color[2];
        }
      };
      vines.forEach((list, k) => {
        const swing = 8.5;
        place(list, (p) => swing * Math.sin(TAU * 2 * phase + p.phase + p.y / 52));
      });
      place(cable, (p) => 8 * Math.sin(TAU * phase + p.y / 30));
      place(tail, () => 8.5 * Math.sin(TAU * phase + 0.6));

      // the lamp pulses
      {
        const level = 0.5 + 0.28 * Math.sin(TAU * 3 * phase) + 0.2 * Math.sin(TAU * 5 * phase + 1.1);
        const steps = Math.round(clamp(level, 0, 1) * 3) / 3; // three small steps, not a smooth fade
        const gain = 1 + 0.1 * steps;
        const [x0, y0, x1, y1] = lamp;
        for (let y = y0; y <= y1; y++) {
          for (let x = x0; x <= x1; x++) {
            const i = idx(x, y);
            const glow = (out[i] * 0.3 + out[i + 1] * 0.59 + out[i + 2] * 0.11) / 255;
            if (glow < 0.42) continue;
            const f = 1 + (gain - 1) * edgeWeight(x, y, lamp, 26);
            out[i] = Math.min(255, out[i] * f);
            out[i + 1] = Math.min(255, out[i + 1] * f);
            out[i + 2] = Math.min(255, out[i + 2] * f);
          }
        }
      }

      // the TV: a slow scan line and a faint flicker
      {
        const [x0, y0, x1, y1] = tv;
        const flick = 1 + 0.036 * Math.round(Math.sin(TAU * 7 * phase) + Math.sin(TAU * 3 * phase + 2));
        const bandY = y0 + ((y1 - y0 + 60) * phase - 30);
        for (let y = y0; y <= y1; y++) {
          const rowBand = Math.abs(y - bandY) < 9 ? 0.93 : 1;
          const f = flick * rowBand;
          if (f === 1) continue;
          for (let x = x0; x <= x1; x++) {
            const i = idx(x, y);
            out[i] = clamp(out[i] * f, 0, 255);
            out[i + 1] = clamp(out[i + 1] * f, 0, 255);
            out[i + 2] = clamp(out[i + 2] * f, 0, 255);
          }
        }
      }

      // a few windows and lanterns of the city dim and come back
      lights.forEach((pixels, k) => {
        const plan = flicker[k];
        if (!plan.on) return;
        let into = (t - plan.start) % LOOP;
        if (into < 0) into += LOOP;
        if (into > plan.length) return;
        // dim in two steps, stay, come back in two steps
        const edge = Math.min(into, plan.length - into) / 0.18;
        const dim = edge >= 1 ? plan.depth : 1 - (1 - plan.depth) * (edge >= 0.5 ? 0.6 : 0.3);
        for (const i of pixels) {
          out[i] = out[i] * dim;
          out[i + 1] = out[i + 1] * dim;
          out[i + 2] = out[i + 2] * dim;
        }
      });

      // the near layer: dust in the warm light
      for (const d of dust) {
        const along = (phase * 1 + d.phase) % 1;
        const px = Math.round((d.x + d.drift * Math.sin(TAU * (phase + d.phase))) / ART) * ART;
        const py = Math.round((d.y - d.rise * along) / ART) * ART;
        const fade = Math.min(along, 1 - along) * 5; // fades in and out so the wrap-around never shows
        const alpha = clamp(fade, 0, 1) * 0.55;
        if (alpha < 0.12) continue;
        for (let yy = 0; yy < d.size; yy++) {
          for (let xx = 0; xx < d.size; xx++) {
            const x = px + xx;
            const y = py + yy;
            if (x < 0 || y < 0 || x >= W || y >= H) continue;
            const i = idx(x, y);
            out[i] = out[i] + (255 - out[i]) * alpha;
            out[i + 1] = out[i + 1] + (214 - out[i + 1]) * alpha;
            out[i + 2] = out[i + 2] + (150 - out[i + 2]) * alpha;
          }
        }
      }

      ctx.putImageData(imageData, 0, 0);
    }

    render(0);
    hero.insertBefore(canvas, hero.firstChild);
    hero.classList.add('has-room-canvas');

    // for tests and tuning: draw any frame by hand
    hero.kocoRoomM = { render, frames: FRAMES, canvas, lights: lights.length, lit: flicker.filter((f) => f.on).length };

    /* ---- play only while the hero is seen and the tab is open ---- */
    let visible = true;
    let running = false;
    let startTime = 0;
    let lastFrame = -1;
    const tick = (now) => {
      if (!running) return;
      const frame = Math.floor(((now - startTime) / 1000) * FPS) % FRAMES;
      if (frame !== lastFrame) {
        lastFrame = frame;
        render(frame);
      }
      window.requestAnimationFrame(tick);
    };
    const play = () => {
      const should = visible && !document.hidden && phone.matches && !reducedMotion.matches;
      if (should && !running) {
        running = true;
        startTime = performance.now() - (lastFrame > 0 ? (lastFrame / FPS) * 1000 : 0);
        window.requestAnimationFrame(tick);
      } else if (!should) {
        running = false;
      }
    };
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          visible = entries.some((e) => e.isIntersecting);
          play();
        },
        { threshold: 0.05 }
      ).observe(hero);
    }
    document.addEventListener('visibilitychange', play);
    phone.addEventListener('change', play);
    reducedMotion.addEventListener('change', play);
    play();
  }
})();
